import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  BreadcrumbNavigation,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
} from "source/shared/components"
import { Menu, MenuButton, MenuLink, MenuList } from "@reach/menu-button"
import { WideLayout } from "source/Layout"
import { Routes, Route, Navigate, Link, Outlet } from "react-router-dom"
import { ProfileNavItems, Text2GivePhoneNumber } from "./types"
import Text2Give from "./text2give"
import Notifications from "./notifications"
import Statements from "./statements"
import DonationHistory from "./donation_history"
import PlannedGiving from "./planned_giving"
import Overview from "./overview"
import WaysToGive from "./components/WaysToGive"
import { useGivingPersonContext } from "./hooks/useGivingPersonContext"
import { css } from "@emotion/react"
import { GivingOrganizationProvider } from "./hooks/useGivingOrganizationContext"
import { DonationsSummaryProvider } from "./hooks/useDonationSummaryContext"
import { useMediaQuery } from "source/giving/hooks/useMediaQuery"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { useDonationHistoryContext } from "./hooks/useDonationHistoryContext"
import ProfileContextProviders from "./components/ProfileContextProviders"

export function Profile() {
  return (
    <ProfileContextProviders>
      <ProfileNavigation />
    </ProfileContextProviders>
  )
}

function ProfileNavigation() {
  const { givingPerson } = useGivingPersonContext()
  const text2GivePhoneNumber: Text2GivePhoneNumber | null =
    givingPerson.text_to_give_phone_number
  const { downloadUrl, showDownloadButton } = useDonationHistoryContext()

  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.SM - 1}px)`)

  const location = useLocation()
  const currentPath = location.pathname
  const viewingDonationHistory = currentPath === "/giving/profile/donations"

  const defaultProfileNavItem = ProfileNavItems[0]

  const personProfileNavItems = ProfileNavItems.filter(
    (item) =>
      item.label !== "Text2Give" ||
      (item.label === "Text2Give" && givingPerson?.can_view_text2give_settings),
  )

  function RouteRequirement({
    requirement,
    redirectToIfRequirementNotMet,
    children,
  }: {
    requirement: boolean
    redirectToIfRequirementNotMet: string
    children: React.ReactNode
  }) {
    return requirement ? (
      children
    ) : (
      <Navigate to={redirectToIfRequirementNotMet} replace />
    )
  }

  const activeNavItem = useMemo(() => {
    return (
      ProfileNavItems.find((item) => item.path === currentPath) ??
      defaultProfileNavItem
    )
  }, [currentPath, defaultProfileNavItem])

  function WithDonationsSummaryContext() {
    return (
      <DonationsSummaryProvider>
        <Outlet />
      </DonationsSummaryProvider>
    )
  }

  return (
    <WideLayout>
      <BreadcrumbNavigation>
        {(!isMobile || activeNavItem.label === "My giving") && (
          <>
            <BreadcrumbPage linkUrl="/me" pageText="Me" />
            <BreadcrumbDividerIcon />
          </>
        )}
        {activeNavItem.label !== "My giving" && (
          <>
            <BreadcrumbPage linkUrl="/giving/profile" pageText="My giving" />
            <BreadcrumbDividerIcon />
          </>
        )}
        <div className="d-n d-b@md breadcrumbs__page">
          <BreadcrumbPage pageText={activeNavItem.label} isActive />
        </div>
        <div className="d-n@md">
          <Menu>
            <MenuButton
              className="select fw-600"
              css={css`
                border: none;
              `}
            >
              {activeNavItem.label}
            </MenuButton>
            <MenuList>
              {personProfileNavItems.map((item, index) => (
                <MenuLink as={Link} to={item.path} key={index}>
                  {item.label === "My giving" ? "Overview" : item.label}
                </MenuLink>
              ))}
            </MenuList>
          </Menu>
        </div>
        <GivingOrganizationProvider>
          <div className="d-f ai-c ml-a">
            <a
              className={`btn sm-btn outline-btn ${
                viewingDonationHistory && showDownloadButton
                  ? "d-if d-n@sm"
                  : "d-n"
              }`}
              href={downloadUrl || ""}
            >
              Download
            </a>
            <div className={viewingDonationHistory ? "d-n d-b@sm" : "d-b"}>
              <WaysToGive
                currentPersonTextToGiveIsConnected={
                  !!text2GivePhoneNumber?.registration_completed
                }
              />
            </div>
          </div>
        </GivingOrganizationProvider>
      </BreadcrumbNavigation>

      <div className="d-f@md">
        <nav className="sub-navigation mr-4 d-n d-b@md">
          {personProfileNavItems.map((item, index) => (
            <Link
              className={`sub-navigation__link ${
                activeNavItem.path === item.path ? "is-active-link" : null
              }`}
              to={item.path}
              key={index}
              tabIndex={0}
            >
              {item.label === "My giving" ? "Overview" : item.label}
            </Link>
          ))}
        </nav>

        <div className="f-1">
          <Routes>
            <Route element={<WithDonationsSummaryContext />}>
              <Route path="/" element={<Overview />} />
            </Route>
            <Route path="/planned" element={<PlannedGiving />} />
            <Route path="/donations" element={<DonationHistory />} />
            <Route
              path="/text-to-give"
              element={
                <RouteRequirement
                  requirement={givingPerson?.can_view_text2give_settings}
                  redirectToIfRequirementNotMet="/giving/profile"
                >
                  <Text2Give />
                </RouteRequirement>
              }
            />
            <Route
              path="/recurring-donations"
              element={<Navigate to="/planned" replace />}
            />
            <Route
              path="/pledges"
              element={<Navigate to="/planned" replace />}
            />
            <Route
              path="/payment-methods"
              element={<Navigate to="/me/profile/payment-methods" replace />}
            />

            <Route path="/notifications" element={<Notifications />} />
            <Route path="/statements" element={<Statements />} />
          </Routes>
        </div>
      </div>
    </WideLayout>
  )
}
